/**
 * Attendance Policy — HR policy document HTML + print.
 * Snapshot/version helpers shared with confirmation / transfer policies.
 */

export {
  clonePolicySnapshot,
  computeVersionOnSave,
  getPolicySnapshotsForDisplay,
  mergePolicySnapshots,
} from "./confirmationPolicyDocument";

import { isPolicyOverrideAttendance } from "../../lib/attendancePolicyConfig";
import { buildApplicabilityProse } from "./confirmationPolicyDocument";
import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function createDocNumberer() {
  return {
    m: 0,
    s: 0,
    nextMajor() {
      this.m++;
      this.s = 0;
      return String(this.m);
    },
    nextSub() {
      this.s++;
      return `${this.m}.${this.s}`;
    },
  };
}

/**
 * @param {object} policy
 * @param {object} options — companyName, branchesList, workflowRoleLabel, shifts, leaveGroups, holidayGroups, weeklyOffGroups, …
 */
export function buildAttendancePolicyDocumentHtml(policy, options = {}) {
  const p = policy || {};
  const org = options.companyName || MASTERS_DOCUMENT_COMPANY_NAME;
  const companyName = escapeHtml(org);
  const companyAddressRaw = options.companyAddress ?? MASTERS_DOCUMENT_COMPANY_ADDRESS;
  const preparedBy = options.preparedBy || "Human Resources";
  const approvedBy = options.approvedBy || "Management";
  const roleLabel =
    options.workflowRoleLabel ||
    ((role) => {
      const map = {
        manager: "Manager",
        hr: "HR",
        management: "Management",
        reporting_manager: "Reporting Manager",
      };
      return map[role] || role || "—";
    });

  const shifts = options.shifts || [];
  const leaveGroups = options.leaveGroups || [];
  const holidayGroups = options.holidayGroups || [];
  const weeklyOffGroups = options.weeklyOffGroups || [];

  const shiftName =
    shifts.find((s) => String(s.id) === String(p.shiftGroupId))?.name || p.shiftGroupId || "—";
  const leaveG = leaveGroups.find((g) => String(g.id) === String(p.leaveGroupId))?.name || "—";
  const holG = holidayGroups.find((g) => String(g.id) === String(p.holidayGroupId))?.name || "—";
  const woG = weeklyOffGroups.find((g) => String(g.id) === String(p.weeklyOffGroupId))?.name || "—";

  const ver = p.policyVersion != null ? p.policyVersion : 1;
  const eff = escapeHtml(p.effectiveDate || "—");
  const policyTitle = escapeHtml(p.policyName || p.name || "Attendance policy");

  const wf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
  const condRules = Array.isArray(p.conditionalWorkflowRules) ? p.conditionalWorkflowRules : [];

  const approvalMethodProse =
    p.approvalMethod === "workflow"
      ? "Leave and related attendance requests follow the multi-step approval workflows configured in this policy."
      : p.approvalMethod === "on_instance"
        ? "Approvals may be decided on a case-by-case basis. Where workflow steps apply, the order and timelines apply to that case."
        : "Eligible requests may be processed without a standing manual chain, subject to HR oversight and system rules.";

  const pushedNote =
    p.policyDocumentPushed && p.policyPushedAt
      ? `<p class="meta"><strong>Employee publication:</strong> This version was published on ${escapeHtml(
          new Date(p.policyPushedAt).toLocaleString()
        )}.</p>`
      : "";

  const addressBlock = companyAddressRaw
    ? `<p class="addr">${escapeHtml(companyAddressRaw).replace(/\n/g, "<br/>")}</p>`
    : "";

  const docNum = createDocNumberer();

  const wfStepsHtml = () => {
    const sec = docNum.nextMajor();
    if (!wf.length) {
      return `<h2>${sec}. Leave and approval workflow</h2><p>No separate workflow steps are defined in this version. HR may apply general approval practice.</p>`;
    }
    let html = `<h2>${sec}. Leave and approval workflow</h2><p>${approvalMethodProse}</p><ol class="steps">`;
    wf.forEach((s, i) => {
      const r = roleLabel(s.role);
      const line = s.notifyOnly
        ? `Notification only — no approval action required from ${escapeHtml(r)}.`
        : `${escapeHtml(r)} must complete the approval within <strong>${s.slaDays ?? 3}</strong> calendar day(s) unless escalated.`;
      html += `<li><p><strong>${docNum.nextSub()} — ${escapeHtml(r)}.</strong></p><p class="step-detail">${line}</p></li>`;
    });
    html += `</ol>`;
    return html;
  };

  const cutOffProse = p.cutOffEnabled
    ? `Attendance cut-off is <strong>enabled</strong> on day <strong>${escapeHtml(
        String(p.cutOffDay ?? p.cutOffToDay ?? 25)
      )}</strong> of each calendar month. Payroll period remains the calendar month. Active employees: pre-cut-off LOP is deducted now and system post-cut-off LOP is deducted next month (manual Change Status LOP is always deducted in the same month). Post-cut-off new joiners (handling <strong>${escapeHtml(
        String(p.cutOffNewJoinerHandling || "defer_lop")
      )}</strong>): payable days after cut-off are held and auto-credited next month as LOP reversal; true LOP stays unpaid. Separation handling: <strong>${escapeHtml(
        String(p.cutOffSeparationHandling || "fnf_recovery")
      )}</strong>.`
    : p.cutOffMethod === "specific"
      ? `Attendance period uses a custom range: from day <strong>${escapeHtml(
          String(p.cutOffFromDay ?? 26)
        )}</strong> of a month to day <strong>${escapeHtml(String(p.cutOffToDay ?? 25))}</strong> of the next month.`
      : "Attendance period follows the <strong>calendar month</strong> (first through last day). Cut-off LOP deferral is <strong>disabled</strong>.";

  const isOverride = isPolicyOverrideAttendance(p);

  const bodySections = isOverride
    ? `
  <h2>${docNum.nextMajor()}. Purpose</h2>
  <p><strong>${docNum.nextSub()}.</strong> This policy places employees in scope on <strong>Override Attendance (manual marking)</strong>.
  Attendance is recorded by authorised users via <strong>Change Status</strong> on the attendance register — not by check-in / check-out punches.</p>
  <p><strong>${docNum.nextSub()}.</strong> Auto attendance (mark all days present) and Override Attendance are both <strong>Yes</strong> for this version.
  Punch channels (mobile, web, biometric, manual entry), location tracking, geo-fencing, IP restriction, device binding, punch mode, grace / hour thresholds, regularization, OT / compensatory off, policy groups, and leave approval workflow are <strong>not applicable</strong> under Override Attendance.</p>

  <h2>${docNum.nextMajor()}. Applicability</h2>
  ${buildApplicabilityProse(p, { ...options, companyName: org })}

  <h2>${docNum.nextMajor()}. Attendance marking</h2>
  <ul>
    <li>Working days default to <strong>Present (PR)</strong> until changed.</li>
    <li>Weekly Off (WO) and Public Holiday (PH) follow <strong>Attendance Settings</strong> (Weekly Off / Holiday tabs), not punch capture.</li>
    <li>Day marks may be corrected with row <strong>Change Status</strong> (and related calendar actions).</li>
    <li>Employee check-in / check-out and punch-driven Present / LOP calculation do not apply.</li>
    <li>Month lock follows <strong>Process Attendance</strong> (existing lock), not policy freeze / cut-off on this path.</li>
  </ul>

  <h2>${docNum.nextMajor()}. Disclaimer</h2>
  <p>This document summarises Override Attendance rules for <strong>${companyName}</strong>. It is not a contract by itself. Contact HR for questions.</p>
`
    : `
  <h2>${docNum.nextMajor()}. Purpose</h2>
  <p><strong>${docNum.nextSub()}.</strong> This policy defines how <strong>${companyName}</strong> records working time, check-in and check-out, lateness, regularization, attendance cut-off for payroll, and linked leave or approval workflows for employees in scope.</p>
  <p><strong>${docNum.nextSub()}.</strong> It applies together with your employment contract, shift assignments, and leave rules. Case-specific decisions remain with management and HR.</p>

  <h2>${docNum.nextMajor()}. Applicability</h2>
  ${buildApplicabilityProse(p, { ...options, companyName: org })}

  <h2>${docNum.nextMajor()}. Check-in, channels, and location</h2>
  <p>Auto attendance (mark all days present): <strong>${p.autoAttendance ? "Yes" : "No"}</strong>.
  Override Attendance (manual marking): <strong>No</strong>.
  Check-in channels — Mobile: <strong>${p.checkInMobile ? "Yes" : "No"}</strong>;
  Web: <strong>${p.checkInWeb ? "Yes" : "No"}</strong>;
  Biometric: <strong>${p.checkInBiometric ? "Yes" : "No"}</strong>.
  Track location: <strong>${p.trackLocation ? "Yes" : "No"}</strong>.
  Restrict online check-in by location: <strong>${p.restrictLocationForOnlineCheckIn ? "Yes" : "No"}</strong>.
  Employee ESS status edit: <strong>${
    !p.autoAttendance
      ? "Not allowed (punch-based)"
      : p.employeeStatusEditEnabled
      ? p.employeeStatusEditRequiresWorkflow !== false
        ? "Allowed via approval workflow"
        : "Allowed — direct edit"
      : "Not allowed"
  }</strong>.</p>

  <h2>${docNum.nextMajor()}. Grace periods and work hours</h2>
  <table class="grid">
    <tr><th>Grace check-in (min)</th><td>${escapeHtml(String(p.graceCheckInMinutes ?? "—"))}</td></tr>
    <tr><th>Grace check-out (min)</th><td>${escapeHtml(String(p.graceCheckOutMinutes ?? "—"))}</td></tr>
    <tr><th>Late after (min)</th><td>${escapeHtml(String(p.considerLateAfterMinutes ?? "—"))}</td></tr>
    <tr><th>Early leave before (min)</th><td>${escapeHtml(String(p.considerEarlyBeforeMinutes ?? "—"))}</td></tr>
    <tr><th>Full day (hours)</th><td>${escapeHtml(String(p.fullDayHours ?? "—"))}</td></tr>
    <tr><th>Half day (hours)</th><td>${escapeHtml(String(p.halfDayHours ?? "—"))}</td></tr>
    <tr><th>Full day if hours ≥</th><td>${escapeHtml(String(p.fullDayIfHoursAtLeast ?? "—"))}</td></tr>
    <tr><th>Half day if hours &lt;</th><td>${escapeHtml(String(p.halfDayIfHoursLessThan ?? "—"))}</td></tr>
  </table>

  <h2>${docNum.nextMajor()}. Sandwich rule</h2>
  <p>${
    p.sandwichRuleEnabled
      ? `Enabled. Weekly off between leave: <strong>${p.sandwichRuleWeeklyOff !== false ? "Yes" : "No"}</strong>;
      Holiday sandwich: <strong>${p.sandwichRuleHoliday !== false ? "Yes" : "No"}</strong>.
      ${(p.sandwichRuleNotes || "").trim() ? escapeHtml(p.sandwichRuleNotes) : ""}`
      : "Sandwich rule is <strong>not</strong> applied under this version."
  }</p>

  <h2>${docNum.nextMajor()}. Regularization limits</h2>
  <p>Regularization allowed: <strong>${p.regularizationAllowed ? "Yes" : "No"}</strong>.
  ${p.regularizationAllowed
    ? `Up to <strong>${escapeHtml(String(p.regularizationTillDays ?? 7))}</strong> day(s) after the attendance date;
    max <strong>${escapeHtml(String(p.regularizationMaxCalendarDaysBack ?? "—"))}</strong> calendar day(s) back from today;
    locked payroll months lookback: <strong>${escapeHtml(String(p.regularizationLockedMonthsMax ?? "—"))}</strong>;
    max punch corrections per month: <strong>${escapeHtml(String(p.regularizationMaxPunchCorrectionsPerMonth ?? "—"))}</strong>;
    max request instances per month: <strong>${escapeHtml(String(p.regularizationMaxRequestInstancesPerMonth ?? "—"))}</strong>.`
    : "Not enabled."}</p>

  <h2>${docNum.nextMajor()}. Overtime and compensatory off</h2>
  <p>${
    p.otCompOffMode && p.otCompOffMode !== "none"
      ? `Mode: <strong>${escapeHtml(p.otCompOffMode)}</strong>.
      ${(p.compensatoryGeneralConditions || "").trim() ? escapeHtml(p.compensatoryGeneralConditions) : "General conditions may be set in the system policy record."}`
      : "No OT / compensatory off policy mode selected in this version."
  }</p>

  <h2>${docNum.nextMajor()}. Alerts and freeze</h2>
  <p>Month-end freeze (days before last day): <strong>${escapeHtml(String(p.attendanceFreezeDaysBeforeMonthEnd ?? 0))}</strong>.
  Missed punch reminder after (hours from shift start): <strong>${escapeHtml(String(p.missedPunchReminderAfterHours ?? "—"))}</strong>.</p>

  <h2>${docNum.nextMajor()}. Attendance cut-off</h2>
  <p>${cutOffProse}</p>

  <h2>${docNum.nextMajor()}. Shift and calendar groups</h2>
  <table class="grid">
    <tr><th>Shift</th><td>${escapeHtml(shiftName)}</td></tr>
    <tr><th>Leave group</th><td>${escapeHtml(leaveG)}</td></tr>
    <tr><th>Holiday group</th><td>${escapeHtml(holG)}</td></tr>
    <tr><th>Weekly off group</th><td>${escapeHtml(woG)}</td></tr>
  </table>
  ${wfStepsHtml()}
  ${
    condRules.length > 0
      ? `<h2>${docNum.nextMajor()}. Conditional workflow rules</h2><p>When a condition matches, the steps below apply instead of the default workflow.</p><ul>${condRules
          .map((rule) => {
            const steps = (rule.workflowSteps || [])
              .map(
                (s) =>
                  `${roleLabel(s.role)}${s.notifyOnly ? " (notify)" : ` — SLA ${s.slaDays ?? 3}d`}`
              )
              .join("; ");
            return `<li><strong>Condition:</strong> ${escapeHtml(rule.conditionType || "")} — ${escapeHtml(
              String(rule.conditionValue ?? "")
            )}. <strong>Steps:</strong> ${escapeHtml(steps || "—")}</li>`;
          })
          .join("")}</ul>`
      : ""
  }

  <h2>${docNum.nextMajor()}. Workflow parameters</h2>
  <p>Reminder days (when workflow applies): <strong>${escapeHtml(String(p.reminderDays ?? 7))}</strong>.
  Parallel approval: <strong>${p.workflowParallelApproval ? "Yes" : "No"}</strong>.
  Escalation after <strong>${escapeHtml(String(p.workflowEscalationAfterDays ?? 3))}</strong> day(s).
  Auto-approval if no action: <strong>${p.workflowAutoApprovalIfNoAction ? "Yes" : "No"}</strong>.
  Rejection with reason: <strong>${p.workflowRejectionWithReason !== false ? "Yes" : "No"}</strong>.
  Re-initiation: <strong>${p.workflowReinitiationAllowed !== false ? "Allowed" : "Restricted"}</strong>.</p>

  <h2>${docNum.nextMajor()}. Disclaimer</h2>
  <p>This document summarises attendance rules for <strong>${companyName}</strong>. It is not a contract by itself. Contact HR for questions.</p>
`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${policyTitle} — ${companyName}</title>
  <style>
    body { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12.5px; line-height: 1.6; color: #1e293b; max-width: 820px; margin: 0 auto; padding: 32px; }
    .letterhead { border-bottom: 2px solid #0f172a; padding-bottom: 18px; margin-bottom: 24px; }
    .company { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 8px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .doc-title { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 15px; font-weight: 700; color: #0f172a; margin: 16px 0 8px 0; text-transform: uppercase; letter-spacing: .06em; }
    .addr { margin: 0; color: #475569; font-size: 11px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .control { margin-top: 14px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 11px; color: #334155; }
    .control strong { color: #0f172a; }
    p.meta { font-size: 11px; color: #64748b; margin-top: 10px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    h2 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 13px; margin: 22px 0 10px 0; color: #0f172a; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    p { margin: 0 0 12px 0; text-align: justify; }
    ul { margin: 8px 0 12px 20px; }
    ol.steps { margin: 8px 0 16px 0; padding: 0; list-style-type: none; }
    ol.steps li { margin-bottom: 10px; }
    p.step-detail { margin: 4px 0 0 12px; font-size: 11.5px; color: #475569; }
    table.grid { width: 100%; border-collapse: collapse; font-size: 11px; margin: 10px 0 16px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    table.grid th, table.grid td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; vertical-align: top; }
    table.grid th { background: #f1f5f9; font-weight: 600; color: #334155; }
    .sign { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 28px; font-size: 11px; color: #475569; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .sign .line { border-top: 1px solid #94a3b8; padding-top: 8px; margin-top: 36px; color: #0f172a; font-weight: 600; }
    @media print { body { padding: 18px; } }
  </style>
</head>
<body>
  <div class="letterhead">
    <p class="company">${companyName}</p>
    ${addressBlock}
    <p class="doc-title">HR attendance policy${isOverride ? " — Override (manual)" : ""}</p>
    <p style='font-family:"Segoe UI",Inter,Arial,sans-serif;font-size:13px;margin:0 0 4px 0;'><strong>${policyTitle}</strong></p>
    <div class="control">
      <strong>Document version:</strong> ${escapeHtml(String(ver))} &nbsp;|&nbsp;
      <strong>Effective from:</strong> ${eff} &nbsp;|&nbsp;
      <strong>Owned by:</strong> ${escapeHtml(preparedBy)} / ${escapeHtml(approvedBy)}
    </div>
    ${pushedNote}
  </div>

  ${bodySections}

  <div class="sign">
    <div>
      <div class="line">Prepared by (${escapeHtml(preparedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
    <div>
      <div class="line">Approved by (${escapeHtml(approvedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
  </div>
</body>
</html>`;
}

export function printAttendancePolicyDocumentHtml(htmlString) {
  const w = window.open("", "_blank");
  if (!w) {
    window.print();
    return;
  }
  w.document.write(htmlString);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 300);
}
